import {
	useEffect, useMemo, useRef, useState,
} from 'react';
import {
	alephToast,
	getObjectsDiff,
	Input,
	NotificationToast,
	NotificationType,
	useDebouncedMemo,
	useOpen,
	isReddit,
	isSpotify,
	getNewAvatar,
	getRandomMainColor,
} from '@aleph/shared-components';
import { useMutation } from '@apollo/client';
import {
	InviteUserToGroupDocument,
	RedditUserGroupsListDocument,
	TransferTeamOwnershipDocument,
	UpdateUserGroupDocument,
	UserAccountLevel,
	UserGroupRole,
	UserGroupRoleInput,
} from 'wb-reddit/graphql/generated';
import {
	AuthGraphUpdateUserGroupDocument,
	UserGroupAccessRole,
	UserAccountLevel as AuthUserAccountLevel,
	AuthGraphTransferGroupOwnershipDocument,
	AuthGraphUserGroupsListDocument,
	AuthGraphAddUserToUserGroupDocument,
} from 'wb-spotify/graphql/authgraph/generated';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import {
	getTeamAccessEntities,
	parseTeamOwnerFromPlatformUser,
	User,
} from 'wb-reddit/app/routes/teams/utils';
import { useFormik } from 'formik';
import useRedditTeamMutationStore from 'wb-reddit/app/routes/teams/hooks/useRedditTeamMutationStore';
import { object, string } from 'yup';
import ProfileImageUploader from 'wb-reddit/app/common/components/profileImageUploader';
import { sortBy, uniqBy } from 'lodash';
import AccessUserListItem from 'wb-reddit/app/routes/teams/components/accessUserListItem';
import AccessBadge from 'wb-reddit/app/common/components/accessBadge';
import { Placeholder } from 'rsuite';
import OwnerChangeDialog from 'wb-reddit/app/routes/advertisers/components/ownerChangeDialog';
import EntityEditInputSuffix from 'wb-reddit/app/common/components/entityEditInputSuffix';
import { MutatorProps } from 'wb-reddit/app/routes/teams/mutator/container';
import { TeamRow } from 'wb-reddit/app/routes/teams/container';
import TeamUsersAutocomplete from 'wb-reddit/app/routes/teams/mutator/teamUsersAutocomplete';
import TeamOwnerAutocomplete from 'wb-reddit/app/routes/teams/mutator/teamOwnerAutocomplete';
import { Trans, useTranslation } from 'react-i18next';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';
import useOwnerOptions from 'wb-reddit/app/routes/teams/mutator/hooks/useOwnerOptions';

const RedditTeamEditMutator = ({ updateRow, setShouldShowSaveBeforeExit, row }: MutatorProps) => {
	const { t } = useTranslation();
	const currPlatformUser = useUserMetaData();
	const [updateGroup, { loading: updating }] = useMutation(
		isReddit()
			? UpdateUserGroupDocument
			: AuthGraphUpdateUserGroupDocument,
		{ errorPolicy: 'all' },
	);
	const [inviteUserToGroup, { loading: invitingUser }] = useMutation(
		isReddit()
			? InviteUserToGroupDocument
			: AuthGraphAddUserToUserGroupDocument,
		{ errorPolicy: 'all' },
	);

	const [ownerTransferOpen, { onOpen: openOwnerTransfer, onClose: closeOwnerTransfer }] = useOpen(false);

	const [transferTeamOwnership] = useMutation(isReddit() ? TransferTeamOwnershipDocument : AuthGraphTransferGroupOwnershipDocument, {
		refetchQueries: [isReddit() ? RedditUserGroupsListDocument : AuthGraphUserGroupsListDocument],
	});

	const { setMutatingTeam } = useRedditTeamMutationStore();

	const initialValues = useRef(row);
	const initialOwner = useRef<User>(
		isReddit()
			? (row?.users?.find(user => user?.userGroupUserRole === UserGroupRole.Owner)?.user
		?? parseTeamOwnerFromPlatformUser(currPlatformUser))
			// @ts-ignore
			: row?.userGroupAccess?.find(user => user?.userGroupAccessRole === UserGroupAccessRole.Owner)?.user,
	);

	const [selectedOwner, setSelectedOwner] = useState(initialOwner.current as User);

	const {
		values,
		handleChange,
		handleBlur,
		setValues,
		setFieldValue,
		submitForm,
		touched,
		errors,
		isSubmitting,
		resetForm,
	} = useFormik({
		initialValues:  initialValues.current,
		initialTouched: {
			userGroupProfilePicture: !!row?.userGroupProfilePicture,
		},
		validationSchema: object().shape({
			userGroupName: string().required(t('sc: team.validation.name', 'Team name is required')),
		}),

		onSubmit: async (submitValues, { setErrors }) => {
			try {
				const variables = isReddit() ? {
					...submitValues,
					userGroupId:             submitValues.userGroupId ?? '',
					// @ts-expect-error:
					// Initial value is a string, if user uploads image it becomes a ProfileImage object
					userGroupProfilePicture: submitValues.userGroupProfilePicture?.file ?? undefined,
				} : {
					userGroupId:   submitValues.userGroupId ?? '',
					userGroupName: submitValues.userGroupName,
				};

				const mutationResponse = await updateGroup({ variables });

				// @ts-ignore
				const validationErrors: Record<string, string> = Object.keys(mutationResponse.errors?.[0]?.form ?? {}).reduce((accu, field) => {
					// @ts-ignore
					accu[field] = t(mutationResponse.errors?.[0]?.form?.[field]?.message, 'Unknown error');

					return accu;
				}, {});

				setErrors(validationErrors);

				if (!Object.keys(validationErrors ?? {})?.length && mutationResponse.errors) {
					alephToast(
						<NotificationToast
							type={NotificationType.Error}
							title={t('re: something went wrong while saving the team', 'Something went wrong while saving the team')}
							description={
								mutationResponse?.errors?.[0]?.message
									? t(mutationResponse?.errors?.[0]?.message)
									: (
										<div>
											{
												Object.values(validationErrors).map(error => (
													<div key={error}>{error}</div>
												))
											}
										</div>
									)

							}
						/>,
					);

					return;
				}

				if (!Object.keys(validationErrors).length) {
					alephToast(
						<NotificationToast
							type={NotificationType.Success}
							title={
								<Trans
									i18nKey="re: team updated successfully"
									defaults="Team <strong>{{name}}</strong> updated successfully"
									values={{
										name: values.userGroupName,
									}}
								>
									Team <strong>{values.userGroupName}</strong> updated successfully
								</Trans>
							}
						/>,
					);

					// Also need to change owner
					if (initialOwner.current?.id !== selectedOwner.id) {
						openOwnerTransfer();
					} else {
						if (mutationResponse.data?.updateUserGroup) {
							updateRow?.({
								row: mutationResponse.data?.updateUserGroup as TeamRow, // TODO: type cast refactor
							});
						}
						setMutatingTeam(undefined);
					}
				}
			} catch (error) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={t('re: something went wrong while saving the team', 'Something went wrong while saving the team')}
						description={
							error instanceof Error ? <div>{t(error.message, 'Unknown error')}</div> : null
						}
					/>,
				);
			}
		},
	});

	const usersList = useMemo(() => {
		if (isReddit()) {
			return uniqBy(
				sortBy(
					values.users ?? [],
					user => user?.userGroupUserRole !== UserGroupRole.Owner,
				),
				user => user?.user?.userId,
			);
		}

		return uniqBy(
			sortBy(
				// @ts-ignore
				values.userGroupAccess ?? [],
				user => user?.userGroupAccessRole !== UserGroupAccessRole.Owner,
			),
			user => user?.user?.userId,
		);
		// @ts-ignore
	}, [values.users, values.userGroupAccess]);

	const dirtyFields = useDebouncedMemo(
		() => {
			const { users, ...restOfValues } = values;
			const { users: initialUsers, ...initialRestOfValues } = initialValues.current;

			const fields = getObjectsDiff(restOfValues, initialRestOfValues, true);

			// TODO remove when profile picture would be ready on BE
			if (isSpotify()) {
				delete fields.userGroupProfilePicture;
				// @ts-ignore
				delete fields.userGroupAccess;
			}

			// Also if owner changed
			setShouldShowSaveBeforeExit(
				!!Object.keys(fields).length
					|| selectedOwner?.userId !== initialOwner.current?.userId,
			);

			return fields;
		},
		[values],
		150,
	);

	useEffect(() => {
		setMutatingTeam(values);
	}, [values]);

	const imageColor = useMemo(() => getRandomMainColor(), []);

	useEffect(() => {
		// TODO remove when profile picture would be ready on BE
		if (isReddit() && !touched.userGroupProfilePicture) {
			getNewAvatar({ entityType: 'Team', background: imageColor })
				.then((newImage) => {
					setFieldValue('userGroupProfilePicture', newImage);
				});
		}
	}, []);

	const { canEditTeam: spotifyCanEditTeam } = useSpotifyRolePermissions();

	const canEditGroup = useMemo(() => {
		if (isReddit()) {
			return currPlatformUser?.userAccountLevel === UserAccountLevel.Admin || values.users?.some?.(
				user => (
					user.userGroupUserRole === UserGroupRole.Owner || user.userGroupUserRole === UserGroupRole.Admin
				) && user?.user?.userId === currPlatformUser?.userId,
			);
		}

		// Platform admin can edit any team
		if ((currPlatformUser?.userAccountLevel as unknown as AuthUserAccountLevel) === AuthUserAccountLevel.PlatformAdmin) {
			return true;
		}

		// @ts-ignore TODO
		return spotifyCanEditTeam && values.userGroupAccess?.some?.(
			user => (
				user?.userGroupAccessRole === UserGroupAccessRole.Owner || user?.userGroupUserRole === UserGroupRole.Admin
			) && user?.user?.userId === currPlatformUser?.userId,
		);
	}, [currPlatformUser, values.users, spotifyCanEditTeam]);

	const ownerOptions = useOwnerOptions({
		users:           values.users,
		// @ts-ignore TODO: bigger type refactor needed
		userGroupAccess: values.userGroupAccess,
		selectedOwner,
	});

	useEffect(() => {
		if (isReddit()) {
			setFieldValue(
				'users',
				uniqBy(
					[{
						user:                       selectedOwner,
						userGroupUserRole:          UserGroupRole.Owner,
						userGroupPendingInviteHash: null,
					}, ...values.users ?? []],
					user => user.user?.userId,
				),
			);
		}
	}, [selectedOwner]);

	const onUserRoleChanged = (userId: string, userRole: UserGroupRoleInput) => {
		if (isReddit()) {
			setValues(currValues => ({
				...currValues,
				users: currValues.users?.map((currUser) => {
					if (currUser?.user?.userId === userId) {
						return {
							...currUser,
							userGroupUserRole: userRole as unknown as UserGroupRole,
						};
					}

					return currUser;
				}) ?? [],
			}));

			return;
		}

		setValues(currValues => {
			const newTeamValues = {
				...currValues,
				// @ts-ignore TODO types
				userGroupAccess: currValues.userGroupAccess?.map((group) => {
					if (group?.user?.userId === userId) {
						return {
							...group,
							userGroupAccessRole: userRole as unknown as UserGroupRole,
						};
					}

					return group;
				}) ?? [],
			};

			updateRow?.({
				row: newTeamValues,
			});

			return newTeamValues;
		});
	};

	const onUserDisconnected = (userId: string) => {
		if (isReddit()) {
			setFieldValue(
				'users',
				values.users?.filter((currUser) => currUser?.user?.userId !== userId) ?? [],
			);

			updateRow?.({
				row: {
					...values,
					users: values.users?.filter((currUser) => currUser?.user?.userId !== userId) ?? [],
				},
			});

			return;
		}

		setFieldValue(
			'userGroupAccess',
			// @ts-ignore TODO types
			values.userGroupAccess?.filter((group) => group?.user?.userId !== userId) ?? [],
		);

		updateRow?.({
			row: {
				...values,
				// @ts-ignore TODO types
				userGroupAccess: values.userGroupAccess?.filter((group) => group?.user?.userId !== userId) ?? [],
			},
		});
	};

	const isUpdatingTeam = isSubmitting || updating;

	return (
		<div className="flex flex-1 min-w-full px-[40px] pb-0 pt-[40px] gap-[40px]">
			{
				ownerTransferOpen
				&& <OwnerChangeDialog
					type="team"
					onSubmit={async () => {
						const { data } = await transferTeamOwnership({
							variables: {
								userId:      selectedOwner?.userId ?? '',
								userGroupId: values.userGroupId ?? '',
							},
						});

						if (data?.transferUserGroupOwnership) {
							if (isReddit()) {
								updateRow?.({
									// @ts-ignore
									row: data?.transferUserGroupOwnership as TeamRow, // TODO: Check type cast
								});
							}

							if (isSpotify()) {
								updateRow?.({
									// @ts-ignore
									row: { ...row, userGroupAccess: data?.transferUserGroupOwnership?.userGroup?.userGroupAccess },
								});
							}
						}

						setMutatingTeam(undefined);
					}}
					onClose={closeOwnerTransfer}
					oldOwner={initialOwner.current}
					newOwner={selectedOwner}
				/>
			}

			<div className="flex-1 w-[25%] flex justify-center">
				<ProfileImageUploader
					onUploadProfileImage={newImage => {
						setFieldValue('userGroupProfilePicture', newImage);
						submitForm();
					}}
					profileImage={values.userGroupProfilePicture}
					readOnly={!canEditGroup || isSpotify()}
					type="Team"
				/>
			</div>
			<div className="flex-[2] min-w-[50%] w-[50%] flex flex-col gap-[24px]
						px-[2px] pb-[150px]"
			>
				<div className="font-semibold text-[14px]">
					{t('re: team personal information', 'Personal information')}
				</div>

				<Input
					id="userGroupName"
					label={t('re: team name', 'Team name')}
					className="!w-full"
					onChange={handleChange}
					onBlur={handleBlur}
					value={values.userGroupName ?? ''}
					validationError={touched.userGroupName ? errors.userGroupName : undefined}
					disabled={!canEditGroup || isUpdatingTeam}
					suffix={
						<EntityEditInputSuffix
							loading={isUpdatingTeam}
							onConfirm={submitForm}
							onCancel={() => resetForm({
								values: {
									...values,
									userGroupName: initialValues.current.userGroupName,
								},
							})}
							showSuffix={dirtyFields.userGroupName}
						/>
					}
				/>

				<TeamOwnerAutocomplete
					options={ownerOptions}
					disabled={
						isUpdatingTeam
						|| (
							// Dont disable if owner was changed
							selectedOwner?.userId === initialOwner.current?.userId
							&& selectedOwner?.userId !== currPlatformUser?.userId
							&& currPlatformUser?.userAccountLevel !== UserAccountLevel.Admin
						)
					}
					onSelect={owner => {
						/**
						 * TODO:
						 * Need to check if owner is valid option ie. has value and label
						 * For some reason when user first clicks the combobox it returns onSelect event
						 */
						if (owner.value && owner.label) {
							// Need to remove prefix, suffix
							const { prefix, suffix, ...rest } = owner;
							setSelectedOwner(rest as unknown as User);
						}
					}}
					value={selectedOwner}
					inputSuffix={
						<EntityEditInputSuffix
							loading={isUpdatingTeam}
							onConfirm={openOwnerTransfer}
							onCancel={() => initialOwner.current && setSelectedOwner(initialOwner.current)}
							showSuffix={selectedOwner?.userId !== initialOwner.current?.userId}
						/>
					}
				/>

				{isReddit() && (
					<Input
						id="salesforceId"
						label={t('re: salesforce customer id', 'Salesforce customer ID')}
						onChange={handleChange}
						onBlur={handleBlur}
						value={values.salesforceId ?? ''}
						validationError={touched.salesforceId ? errors.salesforceId : undefined}
						disabled={!canEditGroup || isUpdatingTeam}
						suffix={
							<EntityEditInputSuffix
								loading={isUpdatingTeam}
								onConfirm={submitForm}
								onCancel={() => resetForm({
									values: {
										...values,
										salesforceId: initialValues.current.salesforceId,
									},
								})}
								showSuffix={dirtyFields.salesforceId}
							/>
						}
					/>
				)}

				<div className="font-semibold text-[14px] pt-[16px]">
					{t('re: team access', 'Access')}
				</div>

				<TeamUsersAutocomplete
					row={row}
					setFieldValue={setFieldValue}
					// @ts-ignore TODO
					inviteUserToGroup={inviteUserToGroup}
					owner={selectedOwner}
					updateRow={updateRow}
				/>

				<div>
					<div className="h-[32px] text-gray-600 text-[11px] items-center px-[8px]
						border-b border-gray-200 flex"
					>
						<div className="flex-1">
							{t('re: team contributor', 'CONTRIBUTOR')}
						</div>
						<div className="px-[8px]">
							{t('re: team domain', 'DOMAIN')}
						</div>
						{canEditGroup && (
							<>
								{values.userGroupId
										&& <div className="w-[80px] flex justify-center">
											{t('re: team admin', 'TEAM ADMIN')}
										</div>
								}
								<div className="w-[60px] text-center">
									{t('re: team revoke', 'REVOKE')}
								</div>
							</>
						)
						}
					</div>

					{usersList?.map((user, key) => (
						<AccessUserListItem
							key={key}
							user={user}
							group={row}
							onUserDisconnected={onUserDisconnected}
							hideActions={!canEditGroup}
							onUserRoleChanged={onUserRoleChanged}
							disabled={isUpdatingTeam}
						/>
					))
					}

					{invitingUser && (
						<div
							className="h-[48px] flex gap-[8px] px-[8px] items-center border-b border-b-gray-200
									last:border-0 hover:bg-gray-100"
						>
							<Placeholder.Graph
								active
								className="!w-[16px] !h-[16px] !min-w-[16px] rounded-full"
							/>

							<Placeholder.Graph
								active
								className="!h-[16px]"
							/>
						</div>
					)
					}
				</div>
			</div>
			<div className="flex-1 w-[25%] flex flex-col gap-[16px]">
				<div className="font-semibold text-[14px]">
					{t('re: team sidebar wise blue', 'WISE.BLUE')}
				</div>

				<div className="flex text-[12px]">
					<div className="w-[80px] flex flex-col gap-[8px] text-gray-600 font-semibold">
						<div>
							{t('re: team sidebar id', 'ID')}
						</div>
						<div>
							{t('re: team sidebar access', 'Access')}
						</div>
					</div>
					<div className="text-black flex flex-col gap-[8px]">
						<div className="text-gray-800">
							{values.userGroupId ?? '-'}
						</div>
						<div>
							{
								values?.userGroupId
									?	<AccessBadge accessEntities={getTeamAccessEntities(row)} />
									: '-'
							}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default RedditTeamEditMutator;
